import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { UIChart } from 'primeng/chart';
import { ThemeService } from '@services/theme.service';
import { ChatChartArtifact } from '../models/orchestrator-chat.models';

Chart.register(...registerables);

@Component({
  selector: 'app-chart-card',
  standalone: true,
  imports: [CommonModule, UIChart],
  template: `
    <div class="chart-card" *ngIf="data">
      <h4 *ngIf="title">{{ title }}</h4>
        <p-chart [type]="chartType" [data]="data" [options]="options" height="220px"></p-chart>
    </div>
  `,
  styles: [`
    .chart-card {
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 12px;
      margin-top: 8px;
    }

    :host-context(body.dark-theme) .chart-card {
      background: #1f2228;
      border-color: #373839;
      color: #f2f2f7;
    }
  `]
})
export class ChartCardComponent implements OnChanges {
  @Input() chart!: ChatChartArtifact;
  data: unknown;
  chartType: 'bar' | 'line' | 'pie' | 'scatter' = 'bar';
  title = '';

  constructor(private readonly themeService: ThemeService) {}

  get options(): object {
    const color = this.themeService.isDarkMode() ? '#f2f2f7' : '#14151a';
    const grid = this.themeService.isDarkMode() ? '#373839' : '#e5e7eb';
    return {
      responsive: true,
      maintainAspectRatio: false,
      color,
      scales: {
        x: {
          ticks: { color, maxRotation: 45, minRotation: 0, autoSkip: true },
          grid: { color: grid }
        },
        y: { ticks: { color }, grid: { color: grid } }
      },
      plugins: { legend: { labels: { color } } }
    };
  }

  ngOnChanges(): void {
    const spec = this.chart?.chart_spec;
    if (!spec) {
      this.data = null;
      return;
    }
    this.title = spec.title ?? '';
    const type = (spec.chart_type || 'bar').toLowerCase();
    this.chartType = type.includes('line') ? 'line' : type.includes('pie') ? 'pie' : type.includes('scatter') ? 'scatter' : 'bar';
    const points = spec.points ?? [];
    this.data = {
      labels: points.map((point) => categoryLabel(point, spec.x)),
      datasets: [
        {
          label: spec.y || spec.title || 'series',
          data: points.map((point) => numericValue(point, spec.y))
        }
      ]
    };
  }
}

const NAME_COLUMNS: Record<string, readonly string[]> = {
  region_id: ['name_ru', 'name_uz', 'name_en'],
  crop_id: ['crop_name_ru', 'crop_name_uz', 'crop_name_en'],
  field_id: ['field_name']
};

/** District, crop, and field charts keep a name on the axis when the point still carries the id. */
function categoryLabel(point: Record<string, unknown>, field: string | undefined): string {
  const named = text(point, NAME_COLUMNS[(field || '').toLowerCase()]);
  if (named) {
    return named;
  }
  return text(point, field ? [field, 'x'] : ['x']) ?? '';
}

function numericValue(point: Record<string, unknown>, field: string | undefined): number {
  for (const key of field ? [field, 'y'] : ['y']) {
    const value = Number(point[key]);
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
}

function text(point: Record<string, unknown>, keys: readonly string[] | undefined): string | null {
  for (const key of keys ?? []) {
    const value = point[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return null;
}
